/**
 * useNarratorLoading Hook
 *
 * Manages tier-aware loading quips with seamless transition to streamed narrator_intro.
 *
 * State Machine:
 * - idle: No analysis in progress
 * - loading_quips: Showing cycling quips (before LLM response)
 * - streaming: Real narrator_intro arriving via stream
 * - complete: Final narrator displayed
 * - fallback: Streaming failed, using summary fallback
 *
 * Usage:
 * ```tsx
 * const { state, displayText, voiceStyle } = useNarratorLoading({
 *   tier: 'drama_plus',
 *   isAnalyzing: true,
 *   streamedNarrator: partialText,
 *   finalNarrator: result?.narrator_intro,
 *   fallbackText: result?.summary,
 * });
 * ```
 *
 * Created: 2026-02-03
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  getQuipConfigForTier,
  getQuipText,
  type UserTier,
  type LoadingQuip,
} from '../lib/narrator/loadingQuips';
import {
  captureNarratorLoadingStarted,
  captureNarratorQuipCycled,
  captureNarratorStreamingStarted,
  captureNarratorDisplayComplete,
} from '../lib/analytics/events/narrator-events';

/**
 * Narrator loading state machine states
 */
export type NarratorLoadingState =
  | 'idle' // No analysis in progress
  | 'loading_quips' // Showing cycling quips
  | 'streaming' // Real narrator_intro arriving
  | 'complete' // Final narrator displayed
  | 'fallback'; // Streaming failed, using summary

export interface UseNarratorLoadingOptions {
  /** User's subscription tier */
  tier: UserTier;
  /** Whether analysis is in progress */
  isAnalyzing: boolean;
  /** Streamed narrator_intro (partial or complete) */
  streamedNarrator?: string;
  /** Final narrator_intro from completed analysis */
  finalNarrator?: string;
  /** Fallback text (usually summary) when streaming fails */
  fallbackText?: string;
  /** Minimum characters before transitioning from quips to streaming */
  streamingThreshold?: number;
  /** Translation function from useTranslation hook */
  t?: (key: string, options?: { defaultValue?: string }) => string;
  /** Theatrical mode for analytics */
  theatricalMode?: 'quick' | 'theatrical' | 'vip';
  /** Session ID for analytics correlation */
  sessionId?: string;
}

export interface UseNarratorLoadingResult {
  /** Current state of the narrator loading */
  state: NarratorLoadingState;
  /** Text to display (quip, streamed, or final) */
  displayText: string;
  /** Current quip being shown (if in loading_quips state) */
  currentQuip: LoadingQuip | null;
  /** Voice style for current tier */
  voiceStyle: 'playful' | 'balanced' | 'professional';
  /** Reset the loading state (e.g., for new analysis) */
  reset: () => void;
}

/**
 * Hook for cycling through tier-aware loading quips
 *
 * Provides seamless transition from loading quips to streamed narrator_intro.
 * Automatically cycles through quips at tier-specific intervals until
 * streaming content arrives or analysis completes.
 */
export function useNarratorLoading({
  tier,
  isAnalyzing,
  streamedNarrator,
  finalNarrator,
  fallbackText,
  streamingThreshold = 10,
  t,
  theatricalMode = 'quick',
  sessionId,
}: UseNarratorLoadingOptions): UseNarratorLoadingResult {
  const config = getQuipConfigForTier(tier);
  const [currentQuipIndex, setCurrentQuipIndex] = useState(() =>
    Math.floor(Math.random() * config.quips.length)
  );
  const [state, setState] = useState<NarratorLoadingState>('idle');
  const cycleIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const previousIsAnalyzing = useRef(isAnalyzing);
  const previousState = useRef<NarratorLoadingState>('idle');
  const analysisStartTime = useRef<number>(0);
  const quipsShownCount = useRef<number>(0);
  const previousQuipId = useRef<string>('');

  /**
   * Reset function for starting a new analysis
   */
  const reset = useCallback(() => {
    setState('idle');
    setCurrentQuipIndex(Math.floor(Math.random() * config.quips.length));
    if (cycleIntervalRef.current) {
      clearInterval(cycleIntervalRef.current);
      cycleIntervalRef.current = null;
    }
  }, [config.quips.length]);

  /**
   * Determine state based on inputs
   * Priority: complete > streaming > loading_quips > idle
   */
  useEffect(() => {
    // Final narrator takes precedence
    if (finalNarrator && finalNarrator.length > 0) {
      setState('complete');
      return;
    }

    // Streaming content detected (above threshold)
    if (streamedNarrator && streamedNarrator.length >= streamingThreshold) {
      setState('streaming');
      return;
    }

    // Analysis in progress, show quips
    if (isAnalyzing) {
      setState('loading_quips');
      return;
    }

    // Not analyzing and no final narrator - check for fallback
    if (!isAnalyzing && !finalNarrator && fallbackText) {
      setState('fallback');
      return;
    }

    // Default to idle
    setState('idle');
  }, [isAnalyzing, streamedNarrator, finalNarrator, fallbackText, streamingThreshold]);

  /**
   * Reset quip index when starting new analysis
   */
  useEffect(() => {
    if (isAnalyzing && !previousIsAnalyzing.current) {
      // Just started analyzing - reset to random quip
      setCurrentQuipIndex(Math.floor(Math.random() * config.quips.length));
      analysisStartTime.current = Date.now();
      quipsShownCount.current = 0;
    }
    previousIsAnalyzing.current = isAnalyzing;
  }, [isAnalyzing, config.quips.length]);

  /**
   * Track state transitions for analytics
   */
  useEffect(() => {
    if (state === previousState.current) return;

    const elapsed = Date.now() - analysisStartTime.current;

    // Track entering loading_quips state
    if (state === 'loading_quips' && previousState.current === 'idle') {
      captureNarratorLoadingStarted({
        tier,
        voiceStyle: config.voiceStyle,
        theatricalMode,
        sessionId,
      });
      quipsShownCount.current = 1; // First quip shown
    }

    // Track entering streaming state
    if (state === 'streaming' && previousState.current === 'loading_quips') {
      captureNarratorStreamingStarted({
        tier,
        voiceStyle: config.voiceStyle,
        timeToFirstChunkMs: elapsed,
        sessionId,
      });
    }

    // Track entering complete state
    if (state === 'complete') {
      captureNarratorDisplayComplete({
        tier,
        voiceStyle: config.voiceStyle,
        source: previousState.current === 'streaming' ? 'streamed' : 'final',
        totalDisplayTimeMs: elapsed,
        quipsShownCount: quipsShownCount.current,
        didStream: previousState.current === 'streaming',
        sessionId,
      });
    }

    // Track entering fallback state
    if (state === 'fallback') {
      captureNarratorDisplayComplete({
        tier,
        voiceStyle: config.voiceStyle,
        source: 'fallback',
        totalDisplayTimeMs: elapsed,
        quipsShownCount: quipsShownCount.current,
        didStream: false,
        sessionId,
      });
    }

    previousState.current = state;
  }, [state, tier, config.voiceStyle, theatricalMode, sessionId]);

  /**
   * Cycle through quips during loading
   */
  useEffect(() => {
    if (state === 'loading_quips') {
      cycleIntervalRef.current = setInterval(() => {
        setCurrentQuipIndex((prev) => {
          const nextIndex = (prev + 1) % config.quips.length;
          const currentQuip = config.quips[prev];
          const nextQuip = config.quips[nextIndex];

          // Track quip cycling for analytics
          // translationAvailable indicates if i18n translations are being used vs hardcoded fallback
          quipsShownCount.current += 1;
          captureNarratorQuipCycled({
            tier,
            voiceStyle: config.voiceStyle,
            quipId: nextQuip.id,
            quipIndex: nextIndex,
            displayDurationMs: config.cycleInterval,
            previousQuipId: currentQuip.id,
            sessionId,
            translationAvailable: !!t,
          });
          previousQuipId.current = currentQuip.id;

          return nextIndex;
        });
      }, config.cycleInterval);

      return () => {
        if (cycleIntervalRef.current) {
          clearInterval(cycleIntervalRef.current);
          cycleIntervalRef.current = null;
        }
      };
    } else {
      // Clear interval when not in loading_quips state
      if (cycleIntervalRef.current) {
        clearInterval(cycleIntervalRef.current);
        cycleIntervalRef.current = null;
      }
    }
  }, [state, config.quips.length, config.cycleInterval, tier, config.voiceStyle, sessionId, config.quips, t]);

  /**
   * Current quip (only valid in loading_quips state)
   */
  const currentQuip =
    state === 'loading_quips' ? config.quips[currentQuipIndex] : null;

  /**
   * Determine display text based on state
   * Uses i18n translations for quips when t() function is provided
   */
  const displayText = (() => {
    switch (state) {
      case 'idle':
        return '';
      case 'loading_quips':
        if (!currentQuip) return '';
        // Use translation if t() provided, otherwise fallback to English
        return t ? getQuipText(currentQuip, t) : currentQuip.fallbackText;
      case 'streaming':
        return streamedNarrator || '';
      case 'complete':
        return finalNarrator || '';
      case 'fallback':
        return fallbackText || '';
      default:
        return '';
    }
  })();

  return {
    state,
    displayText,
    currentQuip,
    voiceStyle: config.voiceStyle,
    reset,
  };
}
